import React from 'react';
import { Box, TextField, Autocomplete, IconButton, Tooltip, Typography } from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { ContentBlock } from '../../../types/blocks';

interface CodeBlockEditorProps {
  block: ContentBlock;
  onChange: (block: ContentBlock) => void;
}

const languages = [
  'javascript', 'typescript', 'python', 'powershell', 'bash', 'sql',
  'json', 'yaml', 'go', 'java', 'csharp', 'hcl', 'plaintext',
];

const CodeBlockEditor: React.FC<CodeBlockEditorProps> = ({ block, onChange }) => {
  const language = (block.meta?.language as string) || 'plaintext';

  const handleCopy = () => {
    navigator.clipboard.writeText(block.content);
  };

  return (
    <Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.5,
          bgcolor: '#1e1e2e',
        }}
      >
        <Autocomplete
          freeSolo
          size="small"
          options={languages}
          value={language}
          onChange={(_, val) => onChange({ ...block, meta: { ...block.meta, language: val || 'plaintext' } })}
          onInputChange={(_, val) => onChange({ ...block, meta: { ...block.meta, language: val || 'plaintext' } })}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              placeholder="Language"
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                sx: { color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' },
              }}
            />
          )}
          sx={{ width: 150 }}
          slotProps={{
            paper: { sx: { fontSize: '0.75rem' } },
          }}
        />
        <Tooltip title="Copy code">
          <IconButton size="small" onClick={handleCopy} sx={{ color: 'rgba(255,255,255,0.5)' }}>
            <CopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <TextField
        fullWidth
        multiline
        minRows={4}
        placeholder="// Paste or type code here..."
        value={block.content}
        onChange={(e) => onChange({ ...block, content: e.target.value })}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
            fontSize: '0.8125rem',
            bgcolor: '#1e1e2e',
            color: '#e2e8f0',
            borderRadius: 0,
            '& fieldset': { border: 'none' },
          },
        }}
      />
    </Box>
  );
};

export default CodeBlockEditor;
